import React from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";

/**
 * The dominant Workspace surface: the center work area and its
 * Chrome-style tabs, as one cohesive card
 * (WORKSPACE-FEATURE-001 S10, S20.5).
 *
 * PR-A-VIS-1 MADE THIS FIXTURE-FREE AND SHARED, RATHER THAN DUPLICATING IT
 * ------------------------------------------------------------------------
 * This component already owned the authoritative center-workspace
 * presentation -- the `glass ... rounded-xl` card, the tab strip, and the
 * active tab visually joining its panel (S20.5). PR-A nevertheless built a
 * second tab host, `WorkspaceCenterTabs`, which copied this file's tab
 * chrome almost verbatim and dropped the card around it. That was the
 * VIS-3 defect.
 *
 * The only thing that genuinely blocked reuse was this file's own fixture
 * coupling: it imported `ScriptPanel` (which pulls `SCRIPT_BLOCKS_FIXTURE`)
 * and defaulted two props from `workspaceViewModel` (which pulls
 * `fakeRuntime` and `workspaceStateStore`). So the fix is to remove the
 * coupling rather than to clone the component: tabs are now injected, and
 * this file imports nothing but React and the shared UI primitive.
 *
 * Each caller supplies what it legitimately has. The development shell
 * passes its fixture panels; Production passes the real
 * `WorkspaceScriptRunner` plus two honest deferred panels. Neither can
 * leak into the other, and there is one center-workspace presentation
 * instead of two.
 *
 * WHY `forceMount` IS PER-TAB RATHER THAN ALWAYS ON
 * -------------------------------------------------
 * Radix unmounts inactive tab content. Production's Script tab must
 * survive a tab switch with its run intact (S10.4), so it opts in. The
 * deferred panels hold no state worth preserving and stay on the default,
 * which keeps the DOM smaller and the contract explicit at the call site
 * instead of implicit here.
 *
 * Pure presentation: no state of its own, no fetch, and no knowledge of
 * the dialer lifecycle.
 */
export default function WorkspaceMainCard({
  tabs,
  activeTab,
  onActiveTabChange,
  className = "",
}) {
  return (
    <div
      className={`glass flex h-full min-h-0 flex-col overflow-hidden rounded-xl ${className}`}
      data-testid="workspace-main-card"
    >
      <Tabs
        value={activeTab}
        onValueChange={onActiveTabChange}
        className="flex h-full min-h-0 flex-1 flex-col"
        data-testid="workspace-center-tabs"
      >
        <TabsList
          aria-label="Workspace sections"
          className="h-11 shrink-0 justify-start gap-1 rounded-none border-b border-white/10 bg-transparent p-0 px-2"
        >
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              data-testid={tab.testId}
              className="h-9 gap-1.5 rounded-b-none rounded-t-[10px] px-3 text-slate-400 data-[state=active]:!bg-indigo-500/15 data-[state=active]:!text-indigo-200 data-[state=active]:!shadow-none"
            >
              {tab.Icon ? <tab.Icon className="h-4 w-4" aria-hidden="true" /> : null}
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((tab) => (
          <TabsContent
            key={tab.value}
            forceMount={tab.forceMount ? true : undefined}
            value={tab.value}
            data-testid={tab.panelTestId}
            className={
              tab.forceMount
                ? "mt-0 flex min-h-0 flex-1 flex-col overflow-y-auto focus-visible:ring-0 data-[state=inactive]:hidden"
                : "mt-0 data-[state=active]:flex data-[state=active]:min-h-0 data-[state=active]:flex-1 data-[state=active]:flex-col data-[state=active]:overflow-y-auto focus-visible:ring-0"
            }
          >
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
